import { Box, Button, Chip, Divider, Icon, IconButton, Paper, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { format } from "date-fns";
import { useState } from "react";
import { useIntl } from "react-intl";

import { StrategyView, useGetStrategy } from "api";
import { Widget } from "components/elements/mui";
import { formatPln } from "components/elements/widgets/Earnings/format";
import { useFormatMessage } from "lang";
import { EARNINGS } from "routes/consts";
import { getSelectedPowerPlant, useAppSelector } from "store";

// Six hours of the switch-off rule applied to the newest nowcast, one cell
// per quarter, so a switch-off shows up as a window on the horizon rather
// than as a two-minute notice.
const OUTLOOK_COLOURS = { off: "error.main", risk: "warning.main", on: "success.main" } as const;

export const CurtailmentOutlook = ({
  outlook,
  legend = false,
  timezone = "Europe/Warsaw",
}: {
  outlook: StrategyView["outlook"];
  // Explain the colours (the earnings page does; the dashboard stays terse).
  legend?: boolean;
  timezone?: string;
}) => {
  const formatMessage = useFormatMessage();
  const theme = useTheme();
  // Hovering a quarter shows its details; a tap or click locks them so the
  // arrows can step through the quarters, as on the charts.
  const [hovered, setHovered] = useState<number | null>(null);
  const [locked, setLocked] = useState<number | null>(null);
  if (!outlook || outlook.slots.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        {formatMessage("strategy.noOutlook")}
      </Typography>
    );
  }
  const hours = Math.round(outlook.slots.length / 4);
  const [first, ...rest] = outlook.windows;
  const selected = locked ?? hovered;
  const slot = selected === null ? null : outlook.slots[selected];
  // "09:45" → "09:45 – 10:00": the quarter the cell stands for.
  const period = (time: string) => {
    const [h, m] = time.split(":").map(Number);
    const end = (h * 60 + m + 15) % (24 * 60);
    return `${time} – ${String(Math.floor(end / 60)).padStart(2, "0")}:${String(end % 60).padStart(2, "0")}`;
  };
  return (
    <Stack gap={0.75}>
      <Typography variant="body2" fontWeight={600} color={first ? "error.main" : "text.primary"}>
        {first
          ? formatMessage("strategy.nextOff", {
              values: { from: first.from, to: first.to, p: Math.round((first.maxProbNegative ?? 0) * 100) },
            }) + (rest.length ? ` · ${formatMessage("strategy.moreWindows", { values: { n: rest.length } })}` : "")
          : formatMessage("strategy.noOff", { values: { hours } })}
      </Typography>
      <Box position="relative" onMouseLeave={() => setHovered(null)}>
        <Stack direction="row" gap="2px" alignItems="flex-end">
          {outlook.slots.map((sl, i) => (
            <Box
              key={sl.start}
              sx={{ flex: 1, minWidth: 0, cursor: "pointer" }}
              onMouseEnter={() => setHovered(i)}
              // The dashboard card navigates on click; a tap on a quarter is
              // a request for its details, not for the earnings page.
              onClick={(e) => {
                e.stopPropagation();
                setLocked(locked === i ? null : i);
              }}
            >
              <Box
                sx={{
                  height: sl.state === "off" ? 16 : sl.state === "risk" ? 12 : 8,
                  borderRadius: 0.5,
                  bgcolor: OUTLOOK_COLOURS[sl.state],
                  opacity: sl.state === "on" && selected !== i ? 0.45 : 1,
                  outline: selected === i ? `2px solid ${theme.palette.text.primary}` : "none",
                  outlineOffset: 1,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: 9,
                  color: i === 0 ? "text.primary" : "text.secondary",
                  fontWeight: i === 0 ? 700 : 400,
                  display: "block",
                  textAlign: "left",
                  lineHeight: 1.4,
                  whiteSpace: "nowrap",
                  visibility: i === 0 || (i % 4 === 0 && i > 1) ? "visible" : "hidden",
                }}
              >
                {i === 0 ? formatMessage("prices.now") : sl.time}
              </Typography>
            </Box>
          ))}
        </Stack>
        {/* Floats above the selected cell like the chart tooltips, so nothing below moves. */}
        {slot && selected !== null && (
          <Paper
            elevation={0}
            onClick={(e) => e.stopPropagation()}
            sx={{
              position: "absolute",
              bottom: "100%",
              mb: 0.5,
              // Wide enough for "Szansa ceny ujemnej" next to "−4151 PLN/MWh"
              // on one line: the panel grows upwards, so a wrapping label
              // would move the arrows between quarters.
              width: 300,
              maxWidth: "100%",
              left: `clamp(0px, calc(${((selected + 0.5) / outlook.slots.length) * 100}% - 150px), calc(100% - 300px))`,
              zIndex: 3,
              border: "solid",
              borderWidth: 2,
              borderColor: "divider",
              backgroundColor: theme.palette.mode === "dark" ? "rgba(18, 18, 18, 0.92)" : "rgba(255, 255, 255, 0.95)",
              px: 1,
              py: 0.5,
            }}
          >
            {/* Same anatomy as the chart tooltips: arrows and period, rows, footer. */}
            <Stack direction="row" alignItems="center" gap={0.25}>
              <IconButton size="small" sx={{ p: 0.25 }} disabled={selected === 0} onClick={() => setLocked(selected - 1)}>
                <Icon sx={{ fontSize: 18 }}>chevron_left</Icon>
              </IconButton>
              <Typography sx={{ flex: 1, textAlign: "center" }}>{period(slot.time)}</Typography>
              <IconButton
                size="small"
                sx={{ p: 0.25 }}
                disabled={selected >= outlook.slots.length - 1}
                onClick={() => setLocked(selected + 1)}
              >
                <Icon sx={{ fontSize: 18 }}>chevron_right</Icon>
              </IconButton>
            </Stack>
            <Divider />
            <Box
              component="table"
              sx={{
                width: "100%",
                borderSpacing: 0,
                my: 1,
                "& td": { py: 0.25, px: 0.5, verticalAlign: "middle", whiteSpace: "nowrap" },
                "& td:last-of-type": { textAlign: "right" },
              }}
            >
              <tbody>
                <tr>
                  <td>
                    <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: OUTLOOK_COLOURS[slot.state] }} />
                  </td>
                  <td>
                    <Typography variant="body2" fontWeight="light">{formatMessage("strategy.tipSimulation")}</Typography>
                  </td>
                  <td>
                    <Typography variant="body2" fontWeight={700} color={OUTLOOK_COLOURS[slot.state]}>
                      {formatMessage(
                        slot.state === "off" ? "strategy.tipVerdictOff" : slot.state === "risk" ? "strategy.tipVerdictRisk" : "strategy.tipVerdictOn",
                      )}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td />
                  <td>
                    <Typography variant="body2" fontWeight="light">{formatMessage("strategy.tipP50")}</Typography>
                  </td>
                  <td>
                    <Typography variant="body2" color={slot.p50 < 0 ? "error.main" : undefined}>
                      {Math.round(slot.p50)} PLN/MWh
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td />
                  <td>
                    <Typography variant="body2" fontWeight="light">{formatMessage("strategy.tipProb")}</Typography>
                  </td>
                  <td>
                    <Typography variant="body2">
                      {slot.probNegative === null ? "—" : `${Math.round(slot.probNegative * 100)}%`}
                    </Typography>
                  </td>
                </tr>
                <tr>
                  <td />
                  <td>
                    <Typography variant="body2" fontWeight="light">{formatMessage("strategy.tipRdn")}</Typography>
                  </td>
                  <td>
                    <Typography variant="body2">{slot.rdn === null ? "—" : `${Math.round(slot.rdn)} PLN/MWh`}</Typography>
                  </td>
                </tr>
              </tbody>
            </Box>
            <Divider />
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pl: 0.5, pt: 0.25 }}>
              <Typography variant="caption" color="text.secondary">
                {formatMessage("strategy.tipIssued", {
                  values: {
                    time: new Intl.DateTimeFormat("pl-PL", { timeZone: timezone, hour: "2-digit", minute: "2-digit" }).format(
                      new Date(outlook.issuedAt),
                    ),
                  },
                })}
              </Typography>
              <Button
                size="small"
                color="inherit"
                sx={{ minWidth: 0, textTransform: "none", py: 0 }}
                onClick={() => {
                  setLocked(null);
                  setHovered(null);
                }}
              >
                {formatMessage("strategy.tipClose")}
              </Button>
            </Stack>
          </Paper>
        )}
      </Box>
      {legend && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
          {formatMessage("strategy.outlookLegend", {
            values: {
              time: new Intl.DateTimeFormat("pl-PL", { timeZone: timezone, hour: "2-digit", minute: "2-digit" }).format(
                new Date(outlook.issuedAt),
              ),
            },
          })}
        </Typography>
      )}
    </Stack>
  );
};

// What to do next, in one card: which settlement to have at Tradea for the
// next changeable day (and whether it is), tomorrow's outlook once its
// prices are out, and how the shadow curtailment rule is doing. Nothing here
// controls the plant.
export const StrategyWidget = ({ disableLink = false }: { disableLink?: boolean }) => {
  const formatMessage = useFormatMessage();
  const intl = useIntl();
  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  const timezone = selectedPowerPlant?.timezone || "Europe/Warsaw";
  const { data, isLoading } = useGetStrategy();

  if (!isLoading && (!data || !data.configured)) return null;
  const s = data?.configured ? data : null;
  const indexLabel = (i: "FIX1" | "CRO" | null) =>
    i === null ? "—" : formatMessage(i === "FIX1" ? "earnings.indexFix1" : "earnings.indexCro");
  const timeIn = (iso: string) =>
    new Intl.DateTimeFormat("pl-PL", { timeZone: timezone, hour: "2-digit", minute: "2-digit" }).format(new Date(iso));
  const dayLabel = (d: string) => format(new Date(`${d}T12:00:00`), "EEEE d.MM");

  return (
    <Widget
      size={{ xs: 12 }}
      title={formatMessage("strategy.title")}
      url={disableLink ? undefined : EARNINGS}
      content={
        !s ? (
          <Box py={0.5}>
            <Skeleton variant="text" width={220} animation="wave" />
            <Skeleton variant="text" width={180} animation="wave" />
          </Box>
        ) : (
          <Stack gap={1.5} py={0.5}>
            <Typography variant="subtitle2">{formatMessage("strategy.simulationTitle")}</Typography>
            <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
              <Typography variant="body2" color="text.secondary">
                {formatMessage("strategy.settlementFor", { values: { date: dayLabel(s.decidable.date) } })}
              </Typography>
              {(s.currentIndex ?? "FIX1") === s.recommendation ? (
                <Chip
                  size="small"
                  color="success"
                  variant="outlined"
                  label={`${indexLabel(s.recommendation)} · ${formatMessage("strategy.noChange")}`}
                />
              ) : (
                <>
                  <Chip
                    size="small"
                    color="warning"
                    label={formatMessage("strategy.changeTo", { values: { index: indexLabel(s.recommendation) } })}
                  />
                  <Typography variant="caption" color="warning.main">
                    {formatMessage("strategy.current")}: {indexLabel(s.currentIndex ?? "FIX1")} ·{" "}
                    {formatMessage("strategy.deadline", { values: { time: `${dayLabel(s.decidable.deadline.slice(0, 10))} ${timeIn(s.decidable.deadline)}` } })}
                  </Typography>
                </>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {s.model
                ? formatMessage("strategy.modelSays", {
                    values: {
                      spread: `${(s.model.expectedSpread ?? 0) > 0 ? "+" : ""}${formatPln(intl, s.model.expectedSpread ?? 0)}`,
                      tail: Math.round((s.model.pTail ?? 0) * 100),
                    },
                  })
                : formatMessage("strategy.modelNone")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatMessage("strategy.record")}:{" "}
              {[s.record30d, s.record90d].every((r) => r.days === 0)
                ? formatMessage("strategy.recordEmpty")
                : [s.record30d, s.record90d]
                    .filter((r) => r.days > 0)
                    .map((r) =>
                      formatMessage("strategy.recordLine", {
                        values: {
                          days: r.days,
                          gain: `${r.followedGainPln > 0 ? "+" : ""}${formatPln(intl, r.followedGainPln)}`,
                          perfect: `+${formatPln(intl, r.perfectGainPln)}`,
                          hit: Math.round((r.hitRate ?? 0) * 100),
                          cro: r.croDays,
                        },
                      }),
                    )
                    .join(" · ")}
            </Typography>

            <Divider />
            <Typography variant="subtitle2">{formatMessage("strategy.shadowTitle")}</Typography>
            <CurtailmentOutlook outlook={s.outlook} />
            <Stack direction="row" gap={2.5} flexWrap="wrap">
              {([
                [formatMessage("strategy.avoidedToday"), s.shadowToday],
                [formatMessage("strategy.avoided7d"), s.shadow7d],
              ] as const).map(([period, t]) => (
                <Stack key={period} minWidth={0}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} letterSpacing={0.5} noWrap>
                    {formatMessage("strategy.simResult")} · {period}
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight={700}
                    lineHeight={1.2}
                    color={t.gainPln < 0 ? "error.main" : t.gainPln > 0 ? "success.main" : "text.primary"}
                  >
                    {t.gainPln > 0 ? "+" : ""}{formatPln(intl, t.gainPln)} PLN
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                    {formatMessage("strategy.simBreakdown", {
                      values: {
                        hits: t.hits, hitGain: `+${formatPln(intl, t.hitGainPln)}`,
                        fa: t.falseAlarms, faCost: formatPln(intl, t.falseAlarmPln),
                        misses: t.misses, missed: formatPln(intl, t.missedPln),
                      },
                    })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                    {formatMessage("strategy.simPossible", { values: { potential: `+${formatPln(intl, t.potentialPln)}` } })}
                    {t.potentialPln > 0 && (
                      <> · {formatMessage("strategy.simCaptured", { values: { share: Math.round((100 * t.gainPln) / t.potentialPln) } })}</>
                    )}
                  </Typography>
                </Stack>
              ))}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
              {formatMessage("strategy.shadowHint")}
            </Typography>
          </Stack>
        )
      }
    />
  );
};
